import React, { useEffect, useState } from 'react';
import { Radio, RadioProps, Input } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import API from '../utils/network';
import RawAPI, { AxiosRequestConfig } from 'axios';
import Button from '../components/elements/Button';
import Uploader from '../components/controls/Uploader';
import UploadProgress, { FileWithID } from '../components/controls/UploadProgress';
import { reject } from 'lodash';
import mime from 'mime/lite';
import { stringIsNullOrUndefinedOrBlank } from '../utils/siteFunctions';

const UploadDocumentsToAgencyPage = ():React.ReactElement => {
	/// Global State
	const customer = useSelector(state => state.ui.customer);
	const client = useSelector(state => state.ui.client);

	/// Local State
	const [ filesToUpload, setFilesToUpload ] = useState<File[]>([]);
	const [ uploadType, setUploadType ] = useState<string>('');
	const [ uploadingFiles, setUploadingFiles ] = useState<FileWithID[]>([]);
	const [ uploadNote, setUploadNote ] = useState('');

	/// Events
	const handleUploaderChange = (files: File[]) => {
		setFilesToUpload(files);
	};

	const handleUploadTypeChange = (e, { value, checked }: RadioProps) => {
		if (checked)
			setUploadType(value as string);
		else
			setUploadType('');
	};

	const handleUploadNoteChange = (e, { value }) => {
		setUploadNote(value);
	};

	const handleUploadClick = e => {
		const preMap = filesToUpload.map((x, index) => ({
			id: uuidv4(),
			file: x
		}));

		setUploadingFiles(prevState => [ ...prevState, ...preMap.map(x => ({ id: x.id, file: x.file, progress: 0, completed: false, type: uploadTypes.reduce((x, current) => x.value === uploadType ? x : current).name})) ]);
		setFilesToUpload([]);

		preMap.forEach((f, i) => {
			const fileExtension = f.file.name.includes('.') ? f.file.name.slice(f.file.name.lastIndexOf('.') + 1) : '';
			const mimeType = mime.getType(fileExtension);

			let addFileEndpoint = '';
			let addFilePostBody: any = undefined;
			let getUrlEndpoint = '';
			
			switch (uploadType) {
			case 'document':
				addFileEndpoint = '/client/client-file/document/add';

				addFilePostBody = {
					ClientId: 0,
					FileLocation: 'S3',
					FileSize: f.file.size,
					FileName: f.file.name,
					FileExtension: `.${fileExtension}`,
					ModifiedDate: new Date(f.file.lastModified),
					ShowInClientPortal: true,
					FileType: mimeType,
					Comment: stringIsNullOrUndefinedOrBlank(uploadNote) ? null : uploadNote
				};

				getUrlEndpoint = '/client/client-file/s3-document-url';
				break;
			case 'portfolio':
				addFileEndpoint = '/client/client-file/portfolio/add';

				addFilePostBody = {
					ClientId: 0,
					FileLocation: 'S3',
					FileSize: f.file.size,
					FileName: f.file.name,
					FileExtension: fileExtension,
					ModifiedDate: new Date(f.file.lastModified),
					FileType: mimeType
				};

				getUrlEndpoint = '/client/client-file/s3-portfolio-url';
				break;
			}

			API.post(addFileEndpoint, addFilePostBody)
				.then(data => {
					const newRecordId = data.data.fileId;

					API.post(getUrlEndpoint, {
						FileId: newRecordId,
						FileExtension: fileExtension,
						ContentType: mimeType
					})
						.then(data => {
							const config: AxiosRequestConfig = {
								headers: { 'content-type': mimeType },
								onUploadProgress: e => setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, progress: e.loaded, completed: e.loaded === x.file.size } : x))
							};

							RawAPI.put(data.data.signedUrl, f.file, config)
								.then(data => { /* Nothing to do here. The progress callback in the config takes it from here. */ })
								.catch(error => {
									console.error('Error', error);
									setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, error: error } : x));
								});
						})
						.catch(error => console.error('Error', error));
				})
				.catch(error => console.error('Error', error));
		});
	};

	/// Effects
	useEffect(() => {
		// setUploadType(uploadTypes[0].value);
		setUploadType('portfolio');
	}, []);

	/// Render
	return (
		<div style={{ padding: '1em' }}>
			<h1>Upload to {customer.customerName}</h1>
			<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<div style={{ padding: '2em', maxWidth: '60em', width: '100%', display: 'flex', flexDirection: 'column' }}>
					<h2 style={{ textAlign: 'center' }}>Bulk Account Upload</h2>
					{/* <div
						style={{
							display: 'flex',
							gap: '1.5em',
							marginBottom: '1em',
							alignItems: 'center'
						}}
					>
						Type of file to upload:
						{uploadTypes.map(x => (
							<Radio key={x.value} value={x.value} label={x.name} name="uploadType" checked={uploadType === x.value} onChange={handleUploadTypeChange} />
						))}
					</div> */}
					<Uploader onChange={handleUploaderChange} value={filesToUpload} />
					{ client.showFieldsSettings.UploadNote && uploadType === 'document' && <Input value={uploadNote} onChange={handleUploadNoteChange} fluid /> }
					<Button secondary onClick={handleUploadClick} style={{ marginTop: '1em' }}>Upload</Button>
				</div>
			</div>
			{uploadingFiles.length > 0 && (
				<>
					<h2>Uploading</h2>
					<UploadProgress uploadingFiles={uploadingFiles} showType={true} />
				</>
			)}
		</div>
	);
};

interface UploadType {
	name: string,
	value: string
}

const uploadTypes: UploadType[] = [
	{
		name: 'Document',
		value: 'document'
	},
	{
		name: 'Portfolio',
		value: 'portfolio'
	}
];

export default UploadDocumentsToAgencyPage;